import type { WorkbenchChange, WorkbenchCounts } from '@/components/Workbench/chartContext'

/**
 * A workbench update as the chat shows it — both the pending one on the
 * composer's "Chart updated" tab and a sent one in the history (where the
 * server returns it without the model text).
 */
export interface WorkbenchUpdateSummary {
  /** e.g. "EURUSD · 1h chart". */
  label: string
  /** The AI had never seen this chart: "Send chart to AI" / "Chart sent to AI". */
  first: boolean
  counts: WorkbenchCounts
  changes: WorkbenchChange[]
  /** Changes left out of `changes` by the size cap. */
  omitted: number
  /** What the user typed in the composer to go with a sent update; absent or '' when nothing. */
  message?: string
}

/**
 * The longest message Send to AI takes with the changes. It fits the headroom the
 * staged update leaves under the server's byte limit (UPDATE_BYTE_BUDGET in
 * chartContext.ts against server-ai's WORKBENCH_LIMITS.bytes), even in 4-byte characters.
 */
export const WORKBENCH_MESSAGE_MAX = 2000

export function totalChanges(counts: WorkbenchCounts): number {
  return counts.added + counts.edited + counts.removed
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/**
 * The composer row's summary of the drawing changes — "drawings: 1 added, 2 edited",
 * or "3 drawings" on a first share (everything is new, so "added" would say nothing).
 * '' when there are none.
 */
export function changesText(summary: Pick<WorkbenchUpdateSummary, 'first' | 'counts'>): string {
  const { counts } = summary
  if (summary.first) return counts.added > 0 ? plural(counts.added, 'drawing', 'drawings') : ''
  const parts = [
    counts.added > 0 ? `${counts.added} added` : null,
    counts.edited > 0 ? `${counts.edited} edited` : null,
    counts.removed > 0 ? `${counts.removed} removed` : null,
  ].filter(Boolean)
  return parts.length > 0 ? `drawings: ${parts.join(', ')}` : ''
}

/** Lenient read of a server/SSE payload — a malformed one renders as an empty card, never throws. */
export function toWorkbenchSummary(raw: unknown): WorkbenchUpdateSummary {
  const r = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>
  const c = (typeof r.counts === 'object' && r.counts !== null ? r.counts : {}) as Record<string, unknown>
  const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : 0)
  return {
    label: typeof r.label === 'string' ? r.label : '',
    first: r.first === true,
    counts: { added: n(c.added), edited: n(c.edited), removed: n(c.removed) },
    changes: Array.isArray(r.changes) ? (r.changes as WorkbenchChange[]) : [],
    omitted: n(r.omitted),
    message: typeof r.message === 'string' ? r.message : '',
  }
}
